import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import './Offers.css';

const Offers = () => {
  const navigate = useNavigate();

  const handleBookClick = () => {
    navigate('/serviceBooking');
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": "SamSoftix IT & Software Special Offers",
    "url": "https://www.samsoftix.com/offers",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "30% Off Online Booking for IT Services & Student Training",
        "description": "Book IT services, coding tutorials, or software support online and get 30% off. Professional developers train students from Punjab College Sambrial, Superior College, Government College Sambrial, COMSATS, and nearby institutions."
      },
      {
        "@type": "Offer",
        "name": "Free 30-Minute IT Consultation",
        "description": "Free consultation for startups, students, and schools to discuss software development, web/app projects, and IT needs."
      },
      {
        "@type": "Offer",
        "name": "Website Development Training",
        "description": "Learn HTML, CSS, JavaScript, React, and front-end development from industry professionals with hands-on projects."
      },
      {
        "@type": "Offer",
        "name": "Mobile App Development Training",
        "description": "Hands-on Flutter and Android/iOS app development training for students, including projects and internship opportunities."
      },
      {
        "@type": "Offer",
        "name": "Game Development Training",
        "description": "Learn Unity, Unreal Engine, and game design principles from professional developers. Includes practical project experience."
      },
      {
        "@type": "Offer",
        "name": "AI/ML & Python Learning",
        "description": "Introductory AI/ML, Python programming, and data science training with practical exercises and mini projects."
      },
      {
        "@type": "Offer",
        "name": "School/College Lab Setup Bundle",
        "description": "Hardware + software installation & maintenance for educational institutions."
      },
      {
        "@type": "Offer",
        "name": "Free IT Assessment for New Clients",
        "description": "Complimentary IT assessment for businesses and schools to identify upgrades and optimizations."
      },
      {
        "@type": "Offer",
        "name": "10% Off Business Software Packages",
        "description": "Discounts for startups & small businesses on ERP, CRM, and software installation packages."
      },
      {
        "@type": "Offer",
        "name": "Free Network Health Check",
        "description": "Optimize your network performance with a free assessment for businesses, schools, and training labs."
      },
      {
        "@type": "Offer",
        "name": "20% Off Hostinger International Hosting",
        "description": "Use referral code E2CQASIMC7DN for global web hosting at discounted rates."
      },
      {
        "@type": "Offer",
        "name": "Student Bootcamps & Internships",
        "description": "Summer coding bootcamps, internships, and project-based learning for students of Punjab College Sambrial, Superior College, COMSATS, and nearby campuses."
      }
    ]
  };

  return (
    <div className="offers-page">
      <Helmet>
        <title>IT & Software Offers + Student Training | SamSoftix Pakistan</title>
        <meta
          name="description"
          content="SamSoftix offers 30% off online IT services, coding tutorials, web/app/game development training, AI/ML courses, and software support for students from Punjab College Sambrial, Superior College, COMSATS, and nearby colleges. Free consultation, internships, workshops, and Hostinger hosting promo included."
        />
        <meta
          name="keywords"
          content="IT services Sambrial, coding tutor Sambrial, software house Pakistan, student training Sambrial, website development course, mobile app development, game development training, AI ML course, internship, Punjab College Sambrial, Superior College, Government College Sambrial, COMSATS students, online booking discount, 30% off IT services, SamSoftix offers, coding bootcamp, software support, school lab setup, business software packages, network health check, Hostinger promo code"
        />
        <link rel="canonical" href="https://www.samsoftix.com/offers" />
        <meta property="og:title" content="IT & Software Offers + Student Training | SamSoftix" />
        <meta property="og:description" content="Book IT services, coding tutorials, web/app/game development, and software support online and get 30% off. Professional developers train students from local colleges and nearby campuses." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.samsoftix.com/offers" />
        <meta property="og:site_name" content="SamSoftix" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IT & Software Offers + Student Training | SamSoftix" />
        <meta name="twitter:description" content="Exclusive 30% discount for online IT services, coding, web/app/game development training, AI/ML courses, and software support. For students from Punjab College Sambrial, Superior College, COMSATS, and nearby campuses." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="offers-hero">
        <h1>Exclusive IT, Development & Training Offers by SamSoftix</h1>
        <p>
          Explore our <strong>limited-time offers</strong> on professional IT services, web, mobile, and game development projects, expert coding tutorials, AI/ML courses, software deployment, and international hosting solutions.
          Perfect for <strong>students, startups, and global clients</strong> looking for <strong>high-quality, scalable, and reliable technology solutions</strong> combined with hands-on learning opportunities.
        </p>

      </section>

      {/* Offers List */}
      <section className="offers-list">
        <div className="offer-box">
          <h3>🎁 Free 30-Minute Consultation</h3>
          <p>Book now and get a <strong>free IT consultation</strong> for your startup, school, or learning program.</p>
        </div>

        <div className="offer-box">
          <h3>💻 30% Off Online Booking</h3>
          <p>Book IT services, coding training, or software support online and get <strong>30% off</strong>. Perfect for students & businesses.</p>
        </div>

        <div className="offer-box">
          <h3>🌐 Website Development Training</h3>
          <p>Learn HTML, CSS, JavaScript, and React from industry professionals with hands-on projects.</p>
        </div>

        <div className="offer-box">
          <h3>📱 Mobile App Development Training</h3>
          <p>Hands-on Flutter & Android/iOS app development training for students with practical project experience.</p>
        </div>

        <div className="offer-box">
          <h3>🎮 Game Development Training</h3>
          <p>Learn Unity, Unreal Engine, and game design principles from certified developers.</p>
        </div>

        <div className="offer-box">
          <h3>🤖 AI/ML & Python Learning</h3>
          <p>Introductory AI/ML, Python programming, and data science training with practical exercises.</p>
        </div>

        <div className="offer-box highlight-offer">
          <h3>🌐 20% Off Hostinger International Hosting</h3>
          <p>Use promo code <strong>E2CQASIMC7DN</strong> for discounted global web hosting.</p>
          <a
            className="cta-btn"
            href="https://hostinger.com?REFERRALCODE=E2CQASIMC7DN"
            target="_blank"
            rel="noopener noreferrer"
          >
            👉 Claim Hostinger Discount
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="offer-cta">
        <h2>Don’t Miss These Student & IT Deals!</h2>
        <p>
          Book online today and get 30% off IT services, coding tutorials, web/app/game development, AI/ML courses, and more.
        </p>
        <button onClick={handleBookClick}>Book Now</button>
      </section>
    </div>
  );
};

export default Offers;
